/**
 * Organization-scoped navigation.
 *
 * Every screen inside the application shell lives under `/org/<handle>/...`,
 * so the organization you are looking at is visible in the address bar, a link
 * can be pasted to a colleague, and two organizations can be open in two tabs
 * without one silently switching the other.
 *
 * WHY A SHIM RATHER THAN REWRITING EVERY PATH
 *
 * Screens say `to="/suppliers"` — the path within an organization — and that
 * is the readable thing for them to say. Prefixing it at every call site would
 * mean roughly eighty template literals, each one an opportunity to forget. So
 * the prefix is applied HERE instead: screens import `Link`, `NavLink` and
 * `useNavigate` from this module rather than from react-router-dom, keep
 * writing organization-relative paths, and land inside the right organization.
 *
 * Outside the shell — the sign-in card, the vendor RFQ portal — there is no
 * provider, the base is empty, and every export is a straight pass-through.
 * The shim is therefore safe to use anywhere, including in shared components
 * that render on both sides of that boundary.
 *
 * The rule itself lives in orgPath.js, which knows nothing about React.
 */
import { createContext, forwardRef, useCallback, useContext, useMemo } from 'react';
import {
  Link as RouterLink,
  NavLink as RouterNavLink,
  useNavigate as useRouterNavigate,
} from 'react-router-dom';
import { orgPath } from './orgPath.js';

export { orgBasePath, orgPath } from './orgPath.js';

/** '' outside an organization, '/org/<handle>' inside one. */
const OrgBaseContext = createContext('');

export function OrgBaseProvider({ base, children }) {
  return <OrgBaseContext.Provider value={base ?? ''}>{children}</OrgBaseContext.Provider>;
}

/** The current organization's URL prefix, or '' when there is none. */
export function useOrgBase() {
  return useContext(OrgBaseContext);
}

/**
 * Resolves a path against the current organization.
 *
 * For the handful of places that need the string itself — a `window.open`, an
 * href to display, a URL handed to somebody outside the app.
 */
export function useOrgPath() {
  const base = useOrgBase();
  return useCallback((to) => orgPath(base, to), [base]);
}

// Refs are forwarded so a caller can move keyboard focus onto a link (the app
// launcher's arrow-key navigation does), exactly as with react-router's own.
export const Link = forwardRef(function Link({ to, ...rest }, ref) {
  const base = useOrgBase();
  return <RouterLink ref={ref} to={orgPath(base, to)} {...rest} />;
});

export const NavLink = forwardRef(function NavLink({ to, ...rest }, ref) {
  const base = useOrgBase();
  return <RouterNavLink ref={ref} to={orgPath(base, to)} {...rest} />;
});

/**
 * `useNavigate`, scoped to the organization.
 *
 * `navigate(-1)` and friends pass straight through: a delta is history, not a
 * path, and prefixing it would be nonsense.
 */
export function useNavigate() {
  const navigate = useRouterNavigate();
  const base = useOrgBase();

  return useMemo(
    () => (to, options) => (typeof to === 'number' ? navigate(to) : navigate(orgPath(base, to), options)),
    [navigate, base]
  );
}
